/**
 * Input modality tracker for the keyboard focus indicator.
 *
 * Stamps `data-input-modality="mouse" | "keyboard"` on <html> from the last
 * pointer or key event. index.css uses it to hide the Modern focus outline
 * (and the sidebar box-shadow ring) on elements that Chrome marks
 * `:focus-visible` after a purely mouse-driven interaction.
 *
 * Why: Radix menus, selects, popovers and dialogs open on pointerdown without
 * ever focusing their trigger, then return focus to the trigger by script on
 * close. Chrome's heuristic treats that script focus as keyboard focus, so a
 * mouse user sees the outline light up on the "..." button every time a menu
 * closes. The attribute flips back to "keyboard" on the next real key press,
 * so Tab or arrow keys immediately reveal the outline again.
 *
 * Text inputs and editable areas are excluded from the CSS rule, because
 * browsers deliberately always show focus on those, even for mouse clicks.
 */

const ATTRIBUTE = 'data-input-modality'

let isInitialized = false

export function initFocusModality(): void {
  if (isInitialized || typeof window === 'undefined') return
  isInitialized = true

  const root = document.documentElement

  const markMouse = () => {
    root.setAttribute(ATTRIBUTE, 'mouse')
  }

  const markKeyboard = (event: KeyboardEvent) => {
    // A modifier held with a click (Cmd+click, Shift+click) is still mouse use.
    if (event.metaKey || event.ctrlKey || event.altKey) return
    root.setAttribute(ATTRIBUTE, 'keyboard')
  }

  window.addEventListener('pointerdown', markMouse, { capture: true })
  window.addEventListener('mousedown', markMouse, { capture: true })
  window.addEventListener('touchstart', markMouse, {
    capture: true,
    passive: true,
  })
  window.addEventListener('keydown', markKeyboard, { capture: true })
}
